/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Skeleton, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import {
  buildRankingStackedSeries,
  formatBRL,
  getRankingChartLayout,
  mapRankingToChartDataset,
  RANKING_SERIES_KEYS,
  TIPO_NOTA_BY_KPI_KEY,
} from './constants';
import {
  ChartEmpty,
  CHART_HEIGHT_RANKING,
  LocalFilterChip,
  RANKING_INLINE_MAX_HEIGHT,
  RANKING_ROW_HEIGHT,
  STACKED_CHART_LEGEND_SIDE,
  toggleLocalCategoria,
} from './chartShared';

const MIN_BAR_WIDTH_FOR_LABEL = 52;

const chartWrapperSx = {
  width: '100%',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  '& .MuiChartsAxis-root': { '& text': { textAnchor: 'start' } },
};

const UsuarioRankingChart = ({ ranking, loading, height, onSelectUsuario }) => {
  const [localCategoriaKey, setLocalCategoriaKey] = useState(null);
  const isExpanded = height != null;
  const dataset = useMemo(() => mapRankingToChartDataset(ranking), [ranking]);
  const allSeries = useMemo(() => buildRankingStackedSeries(), []);

  const maxDisplayTotal = useMemo(() => {
    if (!dataset.length) return 0;
    if (!localCategoriaKey) {
      return Math.max(...dataset.map((row) => Number(row.total) || 0), 0);
    }
    return Math.max(...dataset.map((row) => Number(row[localCategoriaKey]) || 0), 0);
  }, [dataset, localCategoriaKey]);

  const series = useMemo(() => {
    const base = localCategoriaKey
      ? allSeries.filter((s) => s.id === localCategoriaKey || s.dataKey === localCategoriaKey)
      : allSeries;

    const labelSeriesIndex = base.length - 1;
    return base.map((s, index) => {
      if (index !== labelSeriesIndex) return s;
      return {
        ...s,
        barLabelPlacement: 'center',
        barLabel: (item, context) => {
          const width = Number(context?.bar?.width) || 0;
          if (width < MIN_BAR_WIDTH_FOR_LABEL) return null;

          const row = dataset[item.dataIndex];
          if (!row) return null;

          const total = localCategoriaKey
            ? Number(row[localCategoriaKey]) || 0
            : Number(row.total) || 0;

          if (total <= 0) return null;
          if (maxDisplayTotal > 0 && total < maxDisplayTotal * 0.08) return null;
          return formatBRL(total);
        },
      };
    });
  }, [allSeries, dataset, localCategoriaKey, maxDisplayTotal]);

  const chartLayout = useMemo(() => getRankingChartLayout(dataset), [dataset]);
  const scrollableHeight = Math.max(
    CHART_HEIGHT_RANKING,
    dataset.length * RANKING_ROW_HEIGHT + 48,
  );
  const chartHeight = isExpanded ? (height || scrollableHeight) : scrollableHeight;
  const hasData = dataset.length > 0;

  const localLabel = localCategoriaKey
    ? (TIPO_NOTA_BY_KPI_KEY[localCategoriaKey]?.label || localCategoriaKey)
    : null;

  const handleItemClick = useCallback(
    (_event, identifier) => {
      if (!onSelectUsuario || identifier?.dataIndex == null) return;
      const row = dataset[identifier.dataIndex];
      if (row?.usuarioId) onSelectUsuario(row.usuarioId, row.label);
    },
    [dataset, onSelectUsuario],
  );

  const handleLegendClick = useCallback((_event, legendItem) => {
    const seriesId = legendItem?.seriesId;
    if (!seriesId) return;
    const key = RANKING_SERIES_KEYS.some((s) => s.key === seriesId) ? seriesId : null;
    if (!key) return;
    setLocalCategoriaKey((prev) => toggleLocalCategoria(prev, key));
  }, []);

  const chart = (
    <BarChart
      layout="horizontal"
      dataset={dataset}
      series={series}
      yAxis={[
        {
          scaleType: 'band',
          dataKey: 'label',
          width: chartLayout.yAxisWidth,
          tickLabelStyle: { fontSize: 12, textAnchor: 'start' },
        },
      ]}
      xAxis={[{ min: 0, valueFormatter: (value) => formatBRL(value) }]}
      height={chartHeight}
      margin={chartLayout.margin}
      slotProps={{
        legend: {
          ...STACKED_CHART_LEGEND_SIDE,
          onItemClick: handleLegendClick,
        },
        barLabel: {
          style: {
            fill: '#fff',
            fontSize: 11,
            fontWeight: 600,
          },
        },
      }}
      onItemClick={onSelectUsuario ? handleItemClick : undefined}
      sx={{
        width: '100%',
        maxWidth: '100%',
        ml: 0,
        cursor: onSelectUsuario ? 'pointer' : 'default',
        '& .MuiChartsLegend-root button': { cursor: 'pointer' },
      }}
    />
  );

  if (loading) {
    return (
      <Skeleton
        variant="rounded"
        height={isExpanded ? chartHeight : RANKING_INLINE_MAX_HEIGHT}
      />
    );
  }

  if (!hasData) {
    return <ChartEmpty message="Sem dados no período selecionado" />;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <LocalFilterChip
        label={localLabel ? `Categoria: ${localLabel}` : null}
        onClear={() => setLocalCategoriaKey(null)}
      />
      {isExpanded ? (
        <Box sx={chartWrapperSx}>{chart}</Box>
      ) : (
        <Box
          sx={{
            ...chartWrapperSx,
            maxHeight: RANKING_INLINE_MAX_HEIGHT,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {chart}
        </Box>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        Clique na legenda para filtrar categoria; clique na barra para abrir o colaborador
      </Typography>
    </Box>
  );
};

UsuarioRankingChart.propTypes = {
  ranking: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  height: PropTypes.number,
  onSelectUsuario: PropTypes.func,
};

UsuarioRankingChart.defaultProps = {
  ranking: [],
  loading: false,
  height: null,
  onSelectUsuario: null,
};

export default UsuarioRankingChart;
